import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { getIssueTypeFilterLabel } from "@/types/technicalAudit";

export interface TechnicalAuditRollupProps {
	/** Run the rollup reflects — every drill-through scopes back to it. */
	runId: string;
	/** Normalized severity counts from the issues summary (summary.by_severity) —
	    critical already includes high (service's normalizeSeverity). */
	bySeverity: Record<string, number>;
	/** Issue-type counts keyed by raw type (e.g. `alt_text`) from the issues summary. */
	byType: Record<string, number>;
	/** Bounded list length — rows beyond this fold into the "view all" remainder link. */
	maxRows?: number;
}

/**
 * Exception-first rollup of the latest completed technical audit run.
 * Severity summary line + top issue types, each row deep-linking into the
 * existing issue browser with the type preselected AND scoped to the same
 * run: `/audit/technical-issues?view=issues&type={key}&run_id={run.id}`
 * (verified contract, TechnicalAuditIssuesPage.tsx:713-736 — ?type seeds the
 * filter chip, ?view=issues picks the by_issue tab, run_id scopes the run).
 * Row grammar per doc 15 §4b: count (tabular) + humanized issue phrase +
 * chevron; whole-row link, hover:bg-muted, keyboard-focusable <a>.
 */

/** Canonical browser label when mapped; otherwise snake_case → words. */
function typeLabel(type: string): string {
	const label = getIssueTypeFilterLabel(type);
	return label === type ? type.replace(/_/g, " ") : label;
}
export function TechnicalAuditRollup({ runId, bySeverity, byType, maxRows = 6 }: TechnicalAuditRollupProps) {
	const critical = bySeverity.critical ?? 0;
	const warning = bySeverity.warning ?? 0;
	const info = bySeverity.info ?? 0;
	const totalIssues = critical + warning + info;

	const sortedTypes = Object.entries(byType).sort(
		(a, b) => b[1] - a[1] || a[0].localeCompare(b[0]),
	);
	const topTypes = sortedTypes.slice(0, maxRows);
	const typeCount = sortedTypes.length;

	return (
		<div className="p-4">
			<div className="mb-4 flex flex-wrap items-baseline gap-x-6 gap-y-2">
				<div>
					{/* Doc 15 §4b tone rule: destructive only when critical > 0 — a red zero
					    is a false-alarm signifier; zero renders status-success (all-clear). */}
					<span className={`text-dash-title font-semibold tabular ${critical > 0 ? "text-destructive" : "text-[hsl(var(--status-success))]"}`}>{critical}</span>
					<span className="ml-1.5 text-dash-label uppercase tracking-wide text-muted-foreground">Critical/High</span>
				</div>
				<div>
					<span className="text-dash-title font-semibold tabular text-amber-dark">{warning}</span>
					<span className="ml-1.5 text-dash-label uppercase tracking-wide text-muted-foreground">Warning</span>
				</div>
				<div>
					<span className="text-dash-title font-semibold tabular text-muted-foreground">{info}</span>
					<span className="ml-1.5 text-dash-label uppercase tracking-wide text-muted-foreground">Info</span>
				</div>
			</div>

			{totalIssues === 0 ? (
				<p className="text-sm text-[hsl(var(--status-success))]">No issues found in this crawl.</p>
			) : (
				<>
					{/* Doc 15 §4b positive affirmation: critical-free but not issue-free.
					    Crawl date stays in the panel caption — never duplicated here. */}
					{critical === 0 && (
						<p className="mb-2 text-sm text-[hsl(var(--status-success))]">No critical/high issues</p>
					)}
					{topTypes.length > 0 && (
						<div className="space-y-1">
					{topTypes.map(([type, count]) => (
						<Link
							key={type}
							to={`/audit/technical-issues?view=issues&type=${type}&run_id=${runId}`}
							className="group flex items-center gap-3 rounded px-2 py-1.5 text-sm transition-colors hover:bg-muted"
						>
							<span className="tabular w-8 shrink-0 text-right font-medium text-foreground">{count}</span>
							<span className="flex-1 text-foreground">{typeLabel(type)}</span>
							<ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
						</Link>
					))}
					{typeCount > maxRows && (
						<Link
							to={`/audit/technical-issues?view=issues&run_id=${runId}`}
							className="inline-flex items-center gap-1 px-2 pt-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
						>
							View all <span className="tabular">{typeCount}</span> issue types
							<ChevronRight className="h-3 w-3" />
						</Link>
						)}
						</div>
					)}
				</>
			)}
		</div>
	);
}
